import { z } from "zod";
import { privateStorage } from "@/lib/privateStorage";
import type {
  PlatformId,
  PredictionRequest,
  WorldContext,
} from "@/features/prediction/model/types";
import {
  DEFAULT_PLATFORM_BUDGETS,
  MAX_CORE_SELLING_POINTS,
  PLATFORM_IDS,
} from "@/features/prediction/model/types";

export const CATEGORY_VALUES = [
  "美妆",
  "护肤",
  "美食",
  "穿搭",
  "潮玩",
  "数码",
  "宠物",
  "汽车",
  "医疗",
  "教育",
  "母婴",
  "健身",
  "理财",
  "旅行",
] as const;

export const SENTIMENT_VALUES = [
  "viral",
  "positive",
  "neutral",
  "negative",
  "crisis",
] as const;

// 新版产品流程不再暴露能力开关；统一启用完整预测管线，避免旧草稿中的
// false 显式覆盖 V2 Schema 的 true 默认值。
const V2_FULL_PIPELINE_CAPABILITIES = {
  enable_crossplat: true,
  enable_discourse: true,
  enable_brand_memory: true,
  enable_recsys_rl: true,
  enable_groupchat: true,
  enable_kol_ilp: true,
  enable_search_elasticity: true,
} as const satisfies Pick<
  PredictionRequest,
  | "enable_crossplat"
  | "enable_discourse"
  | "enable_brand_memory"
  | "enable_recsys_rl"
  | "enable_groupchat"
  | "enable_kol_ilp"
  | "enable_search_elasticity"
>;

const platformBudgetSchema = z.object({
  douyin: z.coerce.number().min(0).max(10_000_000),
  xhs: z.coerce.number().min(0).max(10_000_000),
  wechat_video: z.coerce.number().min(0).max(10_000_000),
  bilibili: z.coerce.number().min(0).max(10_000_000),
  kuaishou: z.coerce.number().min(0).max(10_000_000),
  tiktok: z.coerce.number().min(0).max(10_000_000),
  instagram: z.coerce.number().min(0).max(10_000_000),
  youtube: z.coerce.number().min(0).max(10_000_000),
});

const predictionFormBaseSchema = z.object({
  caption: z.string().trim(),
  projectName: z.string(),
  taskBriefSource: z.enum(["llm", "mock"]).nullable(),
  category: z.string(),
  lifecycleDays: z.coerce.number().int().min(7).max(90),
  visualStyle: z.string(),
  musicMood: z.string(),
  audienceAge: z.array(z.string()),
  audienceGender: z.array(z.string()),
  audienceCity: z.array(z.string()),
  platformBudgets: platformBudgetSchema.refine(
    (budgets) => Object.values(budgets).some((value) => value > 0),
    "prediction.validation.budget",
  ),
  useLlm: z.boolean(),
  souls: z.coerce.number().int().min(3).max(10_000),
  enableCrossplat: z.boolean(),
  enableDiscourse: z.boolean(),
  enableBrandMemory: z.boolean(),
  enableRecsys: z.boolean(),
  enableGroupchat: z.boolean(),
  discourseComments: z.coerce.number().int().min(3).max(80),
  groupchatAgents: z.coerce.number().int().min(3).max(50),
  groupchatRounds: z.coerce.number().int().min(1).max(15),
  ownBrand: z.string(),
  competitors: z.string(),
  coreSellingPoints: z.string().refine(
    (value) =>
      (splitCoreSellingPoints(value)?.length ?? 0) <=
      MAX_CORE_SELLING_POINTS,
    "prediction.validation.coreSellingPoints",
  ),
  temperature: z.coerce.number().min(-20).max(45),
  rainy: z.boolean(),
  season: z.string(),
  holiday: z.string(),
  sentiment: z.string(),
  worldNews: z.string(),
});

export function createPredictionFormSchema(hasAttachment: boolean) {
  return predictionFormBaseSchema.superRefine((values, context) => {
    if (!hasAttachment && !values.caption) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["caption"],
        message: "prediction.validation.caption",
      });
    }
  });
}

export const predictionFormSchema = createPredictionFormSchema(false);

export type PredictionFormValues = z.infer<typeof predictionFormBaseSchema>;

export const DEFAULT_FORM_VALUES: PredictionFormValues = {
  caption: "",
  projectName: "",
  taskBriefSource: null,
  category: "",
  lifecycleDays: 14,
  visualStyle: "bright",
  musicMood: "upbeat",
  audienceAge: [],
  audienceGender: [],
  audienceCity: [],
  platformBudgets: { ...DEFAULT_PLATFORM_BUDGETS },
  useLlm: true,
  souls: 100,
  enableCrossplat: true,
  enableDiscourse: true,
  enableBrandMemory: true,
  enableRecsys: true,
  enableGroupchat: true,
  discourseComments: 15,
  groupchatAgents: 12,
  groupchatRounds: 5,
  ownBrand: "",
  competitors: "",
  coreSellingPoints: "",
  temperature: 20,
  rainy: false,
  season: "",
  holiday: "",
  sentiment: "neutral",
  worldNews: "",
};

const NICHE_EN: Record<string, string> = {
  美妆: "beauty",
  护肤: "skincare",
  美食: "food",
  穿搭: "fashion",
  潮玩: "chaowan",
  数码: "tech",
  宠物: "pet",
  汽车: "auto",
  医疗: "healthcare",
  教育: "education",
  母婴: "mom",
  健身: "fitness",
  理财: "finance",
  旅行: "travel",
};

function splitList(value: string): string[] | null {
  const values = value
    .split(/[,，;；、\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
  return values.length ? values : null;
}

function splitCoreSellingPoints(value: string): string[] | null {
  // 卖点正文常含逗号，仅使用确认页回显的稳定分隔符拆项。
  const values = value
    .split(/[;；、\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
  return values.length ? values : null;
}

/** 将任务摘要补充到宏观环境接口的文本上下文，保留原始营销文案。 */
export function buildWorldContextCaption(values: PredictionFormValues): string {
  const audience = [
    values.audienceAge.length
      ? `年龄段：${values.audienceAge.join("、")}`
      : null,
    values.audienceGender.length
      ? `性别：${values.audienceGender.join("、")}`
      : null,
    values.audienceCity.length
      ? `城市层级：${values.audienceCity.join("、")}`
      : null,
  ].filter((item): item is string => Boolean(item));
  const parts = [
    `营销文案：${values.caption.trim()}`,
    values.ownBrand.trim() ? `品牌名称：${values.ownBrand.trim()}` : null,
    audience.length ? `目标人群：${audience.join("；")}` : null,
    values.coreSellingPoints.trim()
      ? `核心卖点：${values.coreSellingPoints.trim()}`
      : null,
    values.category ? `营销品类：${values.category}` : null,
  ].filter((item): item is string => Boolean(item));

  return parts.join("\n");
}

export function formValuesToRequest(
  values: PredictionFormValues,
  worldContext: WorldContext | null,
): PredictionRequest {
  const totalBudget = Object.values(values.platformBudgets).reduce(
    (sum, value) => sum + Math.max(0, Number(value) || 0),
    0,
  );
  const platformAlloc = Object.fromEntries(
    PLATFORM_IDS.flatMap((platform) => {
      const budget = Math.max(0, Number(values.platformBudgets[platform]) || 0);
      return budget > 0 ? [[platform, budget / totalBudget]] : [];
    }),
  );
  const category = values.category || null;
  let groupchatOverrides: Record<string, number> | null = null;
  try {
    const stored = privateStorage.getItem("oransim_v3_groupchat_overrides");
    groupchatOverrides = stored
      ? (JSON.parse(stored) as Record<string, number>)
      : null;
  } catch {
    groupchatOverrides = null;
  }

  return {
    creative: {
      caption: values.caption.trim(),
      duration_sec: 15,
      visual_style: values.visualStyle,
      music_mood: values.musicMood,
      has_celeb: false,
    },
    project_name: values.projectName.trim(),
    campaign_objective: "",
    core_selling_points: splitCoreSellingPoints(values.coreSellingPoints) || [],
    source: values.taskBriefSource,
    platform_budgets: { ...values.platformBudgets },
    audience_age_buckets: values.audienceAge.length
      ? values.audienceAge.map(Number)
      : null,
    audience_gender: values.audienceGender.length
      ? values.audienceGender.map(Number)
      : null,
    audience_city_tiers: values.audienceCity.length
      ? values.audienceCity.map(Number)
      : null,
    category,
    target_niches: category ? [category] : null,
    kol_niche: category ? NICHE_EN[category] || null : null,
    total_budget: totalBudget,
    platform_alloc: platformAlloc,
    use_llm: values.useLlm,
    llm_calibrate: true,
    n_souls: values.souls,
    lifecycle_days: values.lifecycleDays,
    daypart: "auto",
    weather_temp_c: values.temperature,
    rainy: values.rainy,
    sentiment: values.sentiment,
    cross_platform_overlap: 0.25,
    ...V2_FULL_PIPELINE_CAPABILITIES,
    discourse_n_comments: values.discourseComments,
    brand_memory_days: 90,
    groupchat_n_agents: values.groupchatAgents,
    groupchat_n_rounds: values.groupchatRounds,
    groupchat_stance_overrides:
      groupchatOverrides && Object.keys(groupchatOverrides).length
        ? groupchatOverrides
        : null,
    own_brand: values.ownBrand.trim() || null,
    competitors: splitList(values.competitors),
    season: values.season || null,
    holiday_context: values.holiday.trim() || null,
    upcoming_holiday: worldContext?.upcoming_holiday || null,
    weather_desc: worldContext?.weather_desc || null,
    sentiment_reason: worldContext?.sentiment_reason || null,
    world_news_summary: splitList(values.worldNews),
    competitor_moves: worldContext?.competitor_moves || null,
    social_trends: worldContext?.social_trends || null,
    policy_regulation: worldContext?.policy_regulation || null,
  };
}

export function requestToFormValues(
  request: PredictionRequest,
): PredictionFormValues {
  const platformBudgets = { ...DEFAULT_PLATFORM_BUDGETS };
  for (const platform of PLATFORM_IDS) {
    platformBudgets[platform] =
      request.platform_budgets?.[platform] ??
      Math.round(
        request.total_budget * (request.platform_alloc[platform] || 0),
      );
  }
  const genders = Array.isArray(request.audience_gender)
    ? request.audience_gender
    : request.audience_gender === null || request.audience_gender === undefined
      ? []
      : [request.audience_gender];
  return {
    caption: request.creative.caption,
    projectName: request.project_name || "",
    taskBriefSource: request.source || null,
    category: request.category || "",
    lifecycleDays: request.lifecycle_days,
    visualStyle: request.creative.visual_style,
    musicMood: request.creative.music_mood,
    audienceAge: request.audience_age_buckets?.map(String) || [],
    audienceGender: genders.map(String),
    audienceCity: request.audience_city_tiers?.map(String) || [],
    platformBudgets,
    useLlm: request.use_llm,
    souls: request.n_souls,
    enableCrossplat: V2_FULL_PIPELINE_CAPABILITIES.enable_crossplat,
    enableDiscourse: V2_FULL_PIPELINE_CAPABILITIES.enable_discourse,
    enableBrandMemory: V2_FULL_PIPELINE_CAPABILITIES.enable_brand_memory,
    enableRecsys: V2_FULL_PIPELINE_CAPABILITIES.enable_recsys_rl,
    enableGroupchat: V2_FULL_PIPELINE_CAPABILITIES.enable_groupchat,
    discourseComments: request.discourse_n_comments,
    groupchatAgents: request.groupchat_n_agents,
    groupchatRounds: request.groupchat_n_rounds,
    ownBrand: request.own_brand || "",
    competitors: request.competitors?.join(", ") || "",
    coreSellingPoints: request.core_selling_points?.join("、") || "",
    temperature: request.weather_temp_c,
    rainy: request.rainy,
    season: request.season || "",
    holiday: request.holiday_context || "",
    sentiment: request.sentiment,
    worldNews: request.world_news_summary?.join("\n") || "",
  };
}

export function requestToWorldContext(
  request: PredictionRequest,
): WorldContext {
  return {
    temp_c: request.weather_temp_c,
    weather_desc: request.weather_desc || undefined,
    season: request.season || undefined,
    holiday_context: request.holiday_context || undefined,
    upcoming_holiday: request.upcoming_holiday || undefined,
    sentiment: request.sentiment,
    sentiment_reason: request.sentiment_reason || undefined,
    world_news_summary: request.world_news_summary || undefined,
    competitor_moves: request.competitor_moves || undefined,
    social_trends: request.social_trends || undefined,
    policy_regulation: request.policy_regulation || undefined,
  };
}

export function estimateDuration(souls: number): string {
  if (souls <= 50) return "15s";
  if (souls <= 200) return "30s";
  if (souls <= 500) return "60s";
  return "90s+";
}

export function enabledPlatforms(
  values: PredictionFormValues,
): PlatformId[] {
  return PLATFORM_IDS.filter((id) => values.platformBudgets[id] > 0);
}
